import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { FiArrowRight } from "react-icons/fi";
import { EmptyState, PageHeader } from "../../components/dashboard/DashboardUI";
import {
  getOperatorAllServicesTickets,
  getTicketTypes,
} from "../../redux/actions/TicketActions";
import { fetchOperatorProcedures } from "../../redux/actions/ProcedureActions";
import { requestTitle } from "../../utils/requestDisplay";
import {
  DETAIL_PAGES,
  SERVICE_PAGES,
  detailState,
} from "../../utils/operatorPages";

const MAX_TO_TAKE = 6;

function OperatorHome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { ticketTypes, operatorAllServicesTickets } = useSelector(
    (state) => state.tickets,
  );
  const { procedures } = useSelector((state) => state.procedures);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.all([
      dispatch(getTicketTypes()),
      dispatch(getOperatorAllServicesTickets(user.token, user.country)),
      dispatch(fetchOperatorProcedures(user.token, user.country)),
    ]).finally(() => setLoading(false));
  }, [dispatch, user?.token, user?.country]);

  const requests = (procedures || []).filter((r) => DETAIL_PAGES[r.type]);
  const toTake = requests
    .filter((r) => r.status === "paid")
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const inProgress = requests.filter((r) => r.status === "in_progress");
  const tickets = operatorAllServicesTickets || [];
  const pendingTickets = tickets.filter((t) => t.status === "initiated");

  // Tickets of each service, the service being known from the ticket type
  const serviceOf = (ticket) =>
    (ticketTypes || [])
      .find((item) => item.code === ticket.reclamationCode)
      ?.service?.toLowerCase() || "odoo";
  const ticketStats = SERVICE_PAGES.map((service) => {
    const own = tickets.filter((ticket) => serviceOf(ticket) === service.code);
    const count = (status) => own.filter((t) => t.status === status).length;

    return {
      ...service,
      pending: count("initiated"),
      inProgress: count("in_progress"),
      done: count("fulfilled"),
    };
  });

  const stats = [
    { label: "Démarches à prendre en charge", value: toTake.length },
    { label: "Démarches en cours", value: inProgress.length },
    { label: "Demandes de services en attente", value: pendingTickets.length },
  ];

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        lead={`Bonjour ${user?.firstname || ""}, voici ce qui attend votre équipe.`}
      />

      <div className="dash-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="dash-card dash-stat">
            <span>{stat.label}</span>
            <strong>{loading ? "…" : stat.value}</strong>
          </div>
        ))}
      </div>

      <div className="dash-home-grid">
        <section className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">À prendre en charge</h2>
            <Link to="/operator/procedures" className="dash-card-link">
              Toutes les démarches <FiArrowRight aria-hidden="true" />
            </Link>
          </div>

          {loading ? (
            <p className="dash-loading">Chargement…</p>
          ) : toTake.length === 0 ? (
            <EmptyState title="Rien en attente">
              Toutes les démarches payées sont prises en charge.
            </EmptyState>
          ) : (
            <ul className="dash-list">
              {toTake.slice(0, MAX_TO_TAKE).map((request) => (
                <li key={request.reference}>
                  <div>
                    <span className="dash-cell-title">
                      {requestTitle(request)}
                    </span>
                    <span className="dash-cell-sub">
                      #{request.reference} · payée le{" "}
                      {moment(request.updatedAt || request.createdAt).format(
                        "DD/MM/YYYY",
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="dash-link-button"
                    onClick={() =>
                      navigate(DETAIL_PAGES[request.type], {
                        state: detailState(request),
                      })
                    }
                  >
                    Ouvrir
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dash-card">
          <h2 className="dash-card-title">Demandes aux services</h2>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>En attente</th>
                  <th>En cours</th>
                </tr>
              </thead>
              <tbody>
                {ticketStats.map((service) => (
                  <tr key={service.code}>
                    <td>
                      <Link to={service.path} className="dash-table-link">
                        {service.label}
                      </Link>
                    </td>
                    <td>
                      {service.pending > 0 ? (
                        <strong>{service.pending}</strong>
                      ) : (
                        "0"
                      )}
                    </td>
                    <td>{service.inProgress}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}

export default OperatorHome;
